import React, { useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { FiBarChart2, FiUsers, FiTrendingUp } from "react-icons/fi";

import Card from "../../Components/Card";
import ChartCard from "../../Components/ChartCard";
import SideCard from "../../Components/SideCard";
import Alert from "../../Components/Alert";
import useDashboardReports from "../../Hooks/Dashboard/useDashboardReports";
import CustomDropdown from "../../Components/CustomDropdown";
import { FaSpinner } from 'react-icons/fa';


const COLORS = ["#C7882B", "#C03F2A", "#1C4130"];

export default function Dashboard() {
  const [period, setPeriod] = useState("24h");

  const { data, loading, error, refreshDashboard, lastUpdated } = useDashboardReports({ period });

  if (loading && !data)
    return (
      <div className="p-2 md:p-4 bg-light min-h-screen direction-rtl flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-main1 text-2xl mx-auto mb-4" />
          <p className="text-main1 font-cairo">جاري تحميل البيانات ...</p>
        </div>
      </div>
    );

  if (error && !data)
    return (
      <div className="p-6 min-h-screen">
        <Alert type="error" message={error.message || "حدث خطأ ما"} onRetry={refreshDashboard} />
      </div>
    );

  if (!data)
    return (
      <div className="p-6 min-h-screen">
        <Alert type="warning" message="لا توجد بيانات متاحة" onRetry={refreshDashboard} />
      </div>
    );

  const { summary, users, providers, insights } = data;

  const providersPie = () =>
    Object.entries(providers).map(([key, val]) => ({
      name: key.toUpperCase(),
      value: val.volume,
    }));

  const peakHoursBar = () =>
    insights.peak_hours.map((h) => ({
      label: h.label,
      transactions: h.transactions,
    }));

  const volumeTrend = () =>
    Object.entries(providers).map(([k, v]) => ({
      name: k.toUpperCase(),
      volume: v.volume,
    }));

  return (
    <div className="p-6 font-cairo min-h-screen">

      {/* العنوان والفلاتر */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-[#1C4130]">لوحة التحكم</h2>

        <div className="flex items-center gap-3 ">
          <CustomDropdown
            options={[
              { value: "24h", label: "24 ساعة" },
              { value: "7d", label: "7 أيام" },
              { value: "30d", label: "30 يوم" },
              { value: "90d", label: "90 يوم" },
              { value: "1y", label: "سنة" },
            ]}
            selectedValue={period}
            onChange={setPeriod}
          />

          <button
            onClick={refreshDashboard}
            disabled={loading}
            className="bg-main1 text-light px-3 py-2  disabled:opacity-50 rounded-2xl"
          >
            تحديث البيانات
          </button>

          {lastUpdated && (
            <span className="text-sm text-main1">
              آخر تحديث: {lastUpdated.toLocaleString("ar-SY")}
            </span>
          )}
        </div>
      </div>

      {/* بطاقات الملخص */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 animate-fadeSlideUp">
        <Card title="إجمالي العمليات" value={summary.total_transactions} />
        <Card title="إجمالي المبالغ" value={`${summary.total_volume.toLocaleString()} `} />
        <Card title="إجمالي العمولات" value={`${summary.total_commission.toLocaleString()} `} />
        <Card title="نسبة النجاح" value={`${summary.success_rate.toFixed(2)}%`} />
        <Card title="نسبة النمو" value={`${summary.growth_rate}%`} />
      </div>

      {/* شبكة رئيسية */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* PieChart */}
          <ChartCard title="توزيع حسب المزود">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={providersPie()}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  label
                >
                  {providersPie().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" align="center" wrapperStyle={{ marginTop: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* BarChart */}
          <ChartCard title="ساعات الذروة">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peakHoursBar()} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tickMargin={10} />
                <YAxis tickMargin={10} />
                <Tooltip />
                <Bar dataKey="transactions" fill="#1C4130" radius={[6, 6, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* LineChart */}
          <ChartCard title="اتجاه الحجم" className="md:col-span-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={volumeTrend()} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                <XAxis dataKey="name" tickMargin={10} />
                <YAxis tickMargin={10} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="volume"
                  stroke="#1C4130"
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2, fill: "#1C4130" }}
                  activeDot={{ r: 6, stroke: "#C03F2A", fill: "#fff" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* العمود الجانبي */}
        <div className="space-y-6">
          <SideCard title="إحصائيات المستخدمين" icon={<FiUsers />}>
            <div className="py-2 border-b">إجمالي المستخدمين: {users.total}</div>
            <div className="py-2 border-b">المستخدمين النشطين: {users.active}</div>
            <div className="py-2 border-b">تسجيلات جديدة: {users.new_registrations}</div>
            <div className="py-2">نسبة النشاط: {users.activity_rate.toFixed(1)}%</div>
          </SideCard>

          <SideCard title="مقارنة المزودين" icon={<FiBarChart2 />}>
            {Object.entries(providers).map(([key, v]) => (
              <div key={key} className="flex justify-between items-center border-b py-2">
                <div>
                  <div className="font-semibold">{key.toUpperCase()}</div>
                  <div className="text-sm text-gray-500">
                    {v.transactions} عمليات — متوسط {Math.round(v.avg_transaction)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold ">{v.volume.toLocaleString()} ل.س</div>
                  <div className="text-sm text-gray-500">عمولة: {v.commission}</div>
                </div>
              </div>
            ))}
          </SideCard>

          <SideCard title="أكثر المستخدمين نشاطًا" icon={<FiUsers />}>
            {insights.top_users.map((u, i) => (
              <div key={i} className="py-2 border-b">
                <div className="font-semibold text-main1">{u.name}</div>
                <div className="text-sm text-gray-500">
                  {u.transactions} عملية — {u.volume.toLocaleString()} ل.س
                </div>
              </div>
            ))}
          </SideCard>

          <SideCard title="رؤى سريعة" icon={<FiTrendingUp />}>
            <div className="text-sm text-gray-500 space-y-1">
              <div>
                الخدمة الأكثر استخدامًا:{" "}
                <span className="font-bold text-main1">{insights.trending.most_used_service}</span>
              </div>
              <div>
                متوسط حجم العملية:{" "}
                <span className="font-bold text-main1 ">
                  {Math.round(insights.trending.avg_transaction_size)}
                </span>
              </div>
              <div>
                معدل العمولة:{" "}
                <span className="font-bold text-main1">{insights.trending.commission_rate.toFixed(2)}</span>
              </div>
            </div>
          </SideCard>
        </div>
      </div>

    </div>
  );
}
